import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { ApiPetProfileAvatarImageSourceEnum } from '../../../api/generated-client/PetProfile-Service-A.v1/models';
import { isUnknownBreed, SFSGenderOptions } from '../../../util/helpers';
import {
  PetInterface,
  PetAvatarOptions,
  AnimalTypeOptions,
  SterilizationOptions,
  PetType,
} from '../../../types/pet';
import { formatDate } from '../DateInputNew/formatDate';
import { Clinic } from '../VetClinic/vetClinicResponse';
import { emptyFormStateDogPetType } from '../util/petFormHelpers';
// Constants to refer to pet.petType.name for reusability
const PetTypeName = {
  dog: 'Dog',
  cat: 'Cat',
  horse: 'Horse',
  smallPet: 'Small Pet',
  bird: 'Bird',
  fish: 'Fish',
  reptile: 'Reptile',
  farmAnimal: 'Large Animal',
};

const checkUsesBreedTerminology = (petType: PetType): boolean =>
  petType.name === PetTypeName.dog ||
  petType.name === PetTypeName.cat ||
  petType.name === PetTypeName.horse;

export type PetFormValues = {
  adopted: boolean;
  adoptionDay: string | undefined;
  allergies: string[];
  avatarBase64?: string;
  avatarId: string;
  avatarUrl: string;
  avatarSource?: ApiPetProfileAvatarImageSourceEnum;
  bannerUrl?: string;
  birthday: string;
  breedName: string;
  gender: SFSGenderOptions;
  image: string | undefined;
  imageBase64?: string;
  isSterilized?: SterilizationOptions;
  derivedGender?: string;
  medicalConditions: string[];
  medications: string[];
  months: string | undefined;
  name: string;
  petTypeId: AnimalTypeOptions;
  // image shown to customer when asking them to confirm if uploaded avatar looks good
  // and when pet profile is in progress
  progressIcon?: string;
  secondaryBreedName: string;
  // temporary blob saved locally before saving to API
  temporaryAvatar?: Blob;
  // temporaryAvatar converted to data URL for customer preview before saving to API
  temporaryAvatarAsDataUrl?: string;
  weight?: number;
  years: string | undefined;
};

export type PetFormFields =
  | 'name'
  | 'gender'
  | 'weight'
  | 'birthday'
  | 'adoptionDay'
  | 'adopted'
  | 'breedName'
  | 'secondaryBreedName'
  | 'years'
  | 'months'
  | 'avatarUrl'
  | 'bannerUrl'
  | 'progressIcon'
  | 'temporaryAvatar'
  | 'temporaryAvatarAsDataUrl'
  | 'avatarBase64'
  | 'avatarId'
  | 'image'
  | 'imageBase64'
  | 'allergies'
  | 'medications'
  | 'medicalConditions'
  | 'isSterilized'
  | 'derivedGender';

type PetFormContextType = {
  formValues: PetFormValues;
  setFormValues: Dispatch<SetStateAction<PetFormValues>>;
  avatarOptions?: PetAvatarOptions;
  petProfileId: number;
  prePopulatedSearchResults: Array<Clinic>;
  setPrePopulatedSearchResults: Dispatch<SetStateAction<Array<Clinic>>>;
  triggerSearchOnload: boolean;
  setTriggerSearchOnload: Dispatch<SetStateAction<boolean>>;
  setFormValue: (valueType: PetFormFields, value: string | number | boolean) => void;
  loadingAvatarOptionsError?: boolean;
  showAddVetSheet: boolean;
  setShowAddVetSheet: Dispatch<SetStateAction<boolean>>;
  showOnboardingVetSheet: boolean;
  setShowOnboardingVetSheet: Dispatch<SetStateAction<boolean>>;
  newClinic: Clinic;
  setNewClinic: Dispatch<SetStateAction<Clinic>>;
  clinicSearchValue: string;
  setClinicSearchValue: Dispatch<SetStateAction<string>>;
  selectedRemoveClinic: Clinic;
  setSelectedRemoveClinic: Dispatch<SetStateAction<Clinic | undefined>>;
  selectedUndoRemoveClinic: boolean;
  setSelectedUndoRemoveClinic: Dispatch<SetStateAction<boolean>>;
  displayAssignErrorSnackbar: boolean;
  setDisplayAssignErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  displayCreateErrorSnackbar: boolean;
  setDisplayCreateErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  displayUnassignErrorSnackbar: boolean;
  setDisplayUnassignErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  showVetListError: boolean;
  setShowVetListError: Dispatch<SetStateAction<boolean>>;
  showUndoSnackbar: boolean;
  setShowUndoSnackbar: Dispatch<SetStateAction<boolean>>;
};

// eslint-disable-next-line unicorn/no-useless-undefined
const PetFormContext = createContext<PetFormContextType | undefined>(undefined);

export const getFormStateFromPetProfile = (petProfile: PetInterface): PetFormValues => {
  const years =
    petProfile.ageInMonths || petProfile.ageInMonths === 0
      ? `${Math.floor(petProfile.ageInMonths / 12)}`
      : undefined;
  const months =
    petProfile?.ageInMonths || petProfile.ageInMonths === 0
      ? `${petProfile?.ageInMonths % 12}`
      : undefined;
  let breedName = '';
  breedName = !checkUsesBreedTerminology(petProfile.petType)
    ? petProfile.petBreedName || 'Other'
    : petProfile.petBreedName;
  return {
    name: petProfile.name,
    gender: petProfile.gender,
    weight: petProfile.weightLbs,
    birthday: formatDate(petProfile.birthday),
    adoptionDay: formatDate(petProfile.adoptionDate),
    adopted: petProfile.adopted,
    breedName: breedName,
    secondaryBreedName:
      petProfile.secondaryBreedName && isUnknownBreed(petProfile.secondaryBreedName)
        ? 'Mixed' // "Mixed / Unknown" is represented in UI as "Mixed" per SFW implementation
        : petProfile.secondaryBreedName || '',
    years,
    months,
    medications: petProfile.medications.map((med) => med.name),
    allergies: petProfile.medicationAllergies.map((med) => med.name),
    medicalConditions: petProfile.medicalConditions.map((med) => med.name),
    image: petProfile.photoUrl,
    petTypeId: `${petProfile.petType?.id}` as AnimalTypeOptions,
    avatarUrl: petProfile.avatarUrl,
    avatarId: `${petProfile.avatarId}`,
    avatarSource: petProfile.avatarSource,
    isSterilized: petProfile.isSterilized,
    derivedGender: petProfile.derivedGender,
  };
};

const PetFormProvider = ({
  children,
  petProfile,
  avatarOptions,
  loadingAvatarOptionsError = false,
}: {
  children: ReactNode;
  petProfile: PetInterface;
  avatarOptions?: PetAvatarOptions;
  loadingAvatarOptionsError?: boolean;
}) => {
  const initialFormState = getFormStateFromPetProfile(petProfile);
  const [formValues, setFormValues] = useState(initialFormState);
  const [showOnboardingVetSheet, setShowOnboardingVetSheet] = useState<boolean>(false);
  const [newClinic, setNewClinic] = useState<Clinic>();
  const [showAddVetSheet, setShowAddVetSheet] = useState(false);
  const [clinicSearchValue, setClinicSearchValue] = useState('');
  const [selectedRemoveClinic, setSelectedRemoveClinic] = useState<Clinic | undefined>();
  const [prePopulatedSearchResults, setPrePopulatedSearchResults] = useState<Array<Clinic>>([]);
  const [triggerSearchOnload, setTriggerSearchOnload] = useState<boolean>(false);

  const [selectedUndoRemoveClinic, setSelectedUndoRemoveClinic] = useState<boolean>(false);
  const [displayAssignErrorSnackbar, setDisplayAssignErrorSnackbar] = useState<boolean>(false);
  const [displayCreateErrorSnackbar, setDisplayCreateErrorSnackbar] = useState<boolean>(false);
  const [displayUnassignErrorSnackbar, setDisplayUnassignErrorSnackbar] = useState<boolean>(false);
  const [showVetListError, setShowVetListError] = useState<boolean>(false);
  const [showUndoSnackbar, setShowUndoSnackbar] = useState(false);

  const setFormValue = (valueType: PetFormFields, value: string | number | boolean) => {
    setFormValues({ ...formValues, [valueType]: value });
  };

  const petProfileId = petProfile.id;

  const value = {
    formValues,
    setFormValues,
    petProfileId,
    avatarOptions,
    loadingAvatarOptionsError,
    setFormValue,
    showOnboardingVetSheet,
    setShowOnboardingVetSheet,
    newClinic,
    setNewClinic,
    showAddVetSheet,
    setShowAddVetSheet,
    clinicSearchValue,
    setClinicSearchValue,
    selectedRemoveClinic,
    setSelectedRemoveClinic,
    selectedUndoRemoveClinic,
    setSelectedUndoRemoveClinic,
    prePopulatedSearchResults,
    setPrePopulatedSearchResults,
    triggerSearchOnload,
    setTriggerSearchOnload,
    displayAssignErrorSnackbar,
    setDisplayAssignErrorSnackbar,
    displayCreateErrorSnackbar,
    setDisplayCreateErrorSnackbar,
    displayUnassignErrorSnackbar,
    setDisplayUnassignErrorSnackbar,
    showVetListError,
    setShowVetListError,
    showUndoSnackbar,
    setShowUndoSnackbar,
  };

  return <PetFormContext.Provider value={value}>{children}</PetFormContext.Provider>;
};

function usePetForm() {
  const context = useContext(PetFormContext);

  if (context === undefined) {
    throw new Error('usePetForm must be used within a PetFormProvider');
  }

  return context;
}

export { PetFormProvider, usePetForm };
